import { RoutePath } from '@/core/config/routeConfig';
import { ApiUserRole, type ApiUserRole as ApiUserRoleType } from '@/core/generated/api';
import type { SidebarIconName } from './SidebarIcon';
/** Описывает ссылку на раздел админки */
export interface NavItem {
    title: string;
    path: string;
    icon: SidebarIconName;
    allowedRoles?: readonly ApiUserRoleType[];
    accessRoute?: string;
}

/** Объединяет разделы бокового меню */
export interface NavGroup {
    title: string;
    items: NavItem[];
    subgroups?: NavGroup[];
}

/** Оставляет доступные страницы и содержащие их группы на каждом уровне меню */
export const filterNavigationGroups = (
    groups: readonly NavGroup[],
    canViewItem: (item: NavItem) => boolean,
): NavGroup[] => groups
    .map(group => ({
        ...group,
        items: group.items.filter(canViewItem),
        subgroups: group.subgroups
            ? filterNavigationGroups(group.subgroups, canViewItem)
            : undefined,
    }))
    .filter(group => group.items.length > 0 || Boolean(group.subgroups?.length));

/** Главная страница админки */
export const mainNavItem: NavItem = {
    title: 'Главная',
    path: RoutePath.main,
    icon: 'home',
};

/** Общий задачник операторов */
export const operatorTasksNavItem: NavItem = {
    title: 'Задачи',
    path: RoutePath.operatorTasks,
    icon: 'task',
};

/** Группы доступных разделов навигации */
export const navGroups: NavGroup[] = [
    {
        title: 'Пользователи',
        items: [
            {
                title: 'Клиенты',
                path: RoutePath.clients,
                icon: 'client',
            },
            {
                title: 'Исполнители',
                path: RoutePath.executors,
                icon: 'executor',
            },
            {
                title: 'Проблемные исполнители',
                path: RoutePath.problemExecutors,
                icon: 'executor',
            },
        ],
    },
    {
        title: 'Чаты и диалоги',
        items: [
            {
                title: 'Диалоги',
                path: RoutePath.chats,
                icon: 'chat',
            },
            {
                title: 'Шаблоны сообщений чата',
                path: RoutePath.chatReplyTemplates,
                icon: 'chat',
            },
            {
                title: 'Переписки пользователей',
                path: RoutePath.userChats,
                accessRoute: RoutePath.chats,
                icon: 'chat',
            },
        ],
    },
    {
        title: 'Заказы',
        items: [
            {
                title: 'Заказы',
                path: RoutePath.orders,
                icon: 'order',
            },
            {
                title: 'Проблемные заказы',
                path: RoutePath.orderProblems,
                icon: 'order',
            },
            {
                title: 'Проблемные адреса',
                path: RoutePath.problemAddresses,
                icon: 'city',
            },
            {
                title: 'Договор к заданию',
                path: RoutePath.taskIndex,
                icon: 'task',
            },
        ],
    },
    {
        title: 'Продукты и услуги',
        items: [
            {
                title: 'Продукты',
                path: RoutePath.products,
                icon: 'product',
            },
            {
                title: 'Услуги VOX',
                path: RoutePath['vox-services'],
                icon: 'product',
            },
            {
                title: 'Прайс-листы',
                path: RoutePath.priceLists,
                icon: 'price',
            },
            {
                title: 'Абонементы',
                path: RoutePath.subscriptions,
                icon: 'subscription',
            },
        ],
    },
    {
        title: 'Программа лояльности',
        items: [],
        subgroups: [
            {
                title: 'Промокоды',
                items: [
                    {
                        title: 'Промокоды',
                        path: RoutePath.promoCodes,
                        icon: 'promo',
                    },
                    {
                        title: 'Шаблоны промокодов',
                        path: RoutePath.promoCodeTemplates,
                        icon: 'promo',
                    },
                    {
                        title: 'Тиражи промокодов',
                        path: RoutePath.promoCodePrintings,
                        icon: 'promo',
                    },
                ],
            },
            {
                title: 'Cashback',
                items: [
                    {
                        title: 'Уровни кэшбэка',
                        path: RoutePath.cashbackLevels,
                        icon: 'cashback',
                    },
                    {
                        title: 'Правила кэшбэка',
                        path: RoutePath.cashbackRules,
                        icon: 'cashback',
                    },
                    {
                        title: 'Бонусные счета',
                        path: RoutePath.bonusAccounts,
                        icon: 'bonus',
                    },
                ],
            },
        ],
    },
    {
        title: 'Контент и справочники',
        items: [
            {
                title: 'Медиатека',
                path: RoutePath.mediaLibrary,
                icon: 'blog',
            },
            {
                title: 'Города',
                path: RoutePath.cities,
                icon: 'city',
            },
            {
                title: 'Блоги',
                path: RoutePath.blogs,
                icon: 'blog',
            },
            {
                title: 'Шаблоны промобаннеров',
                path: RoutePath.promotionBannerTemplates,
                icon: 'blog',
            },
        ],
    },
    {
        title: 'Системное управление',
        items: [
            {
                title: 'Настройки',
                path: RoutePath.settings,
                icon: 'settings',
            },
            {
                title: 'Текстовки приложения',
                path: RoutePath.messageTemplates,
                icon: 'settings',
            },
            {
                title: 'Диалоговые окна',
                path: RoutePath.userDialogs,
                icon: 'notification',
            },
            {
                title: 'Планировщик задач',
                path: RoutePath.scheduler,
                icon: 'settings',
            },
            {
                title: 'Фича-флаги',
                path: RoutePath.featureToggles,
                icon: 'settings',
            },
            {
                title: 'Версии приложений',
                path: RoutePath.appVersions,
                icon: 'version',
            },
            {
                title: 'Ссылки и ключи',
                path: RoutePath.tags,
                icon: 'tag',
            },
            {
                title: 'Политики',
                path: RoutePath.policies,
                icon: 'policy',
            },
            {
                title: 'Управление доступом',
                path: RoutePath.accessManagement,
                icon: 'access',
                allowedRoles: [ApiUserRole.SuperAdmin, ApiUserRole.Developer],
            },
        ],
    },
    {
        title: 'Push-уведомления',
        items: [
            {
                title: 'Проверка push',
                path: RoutePath.pushTests,
                icon: 'notification',
            },
            {
                title: 'Шаблоны пуш-уведомлений',
                path: RoutePath.notificationTemplates,
                icon: 'notification',
            },
        ],
    },
    {
        title: 'Обслуживание',
        items: [
            {
                title: 'Диагностика памяти',
                path: RoutePath.memoryDiagnostics,
                icon: 'memory',
            },
            {
                title: 'Управление кэшем',
                path: RoutePath.cacheManagement,
                icon: 'settings',
            },
            {
                title: 'Настройки Telegram',
                path: RoutePath.telegramSettings,
                icon: 'telegram',
            },
        ],
    },
    {
        title: 'Статистика и аналитика',
        items: [
            {
                title: 'Статистика отправок',
                path: RoutePath.messageSendStatistics,
                icon: 'statistics',
            },
            {
                title: 'Аналитика отменённых заказов',
                path: RoutePath.orderCancellationAnalytics,
                icon: 'statistics',
            },
        ],
    },
];

/** Описывает раздел в пути текущей страницы */
export interface NavigationCrumb {
    label: string;
    path?: string;
}

/** Возвращает путь страницы по разделам бокового меню */
export const getNavigationTrail = (pathname: string): NavigationCrumb[] => {
    /** Определяет раздел текущей страницы */
    const matches = (item: NavItem) => pathname === item.path || pathname.startsWith(item.path + '/');
    let item = [mainNavItem, operatorTasksNavItem].find(matches);
    const trail: NavigationCrumb[] = [];

    for (const group of navGroups) {
        const direct = group.items.find(matches);
        const subgroup = group.subgroups?.find(sub => sub.items.some(matches));
        const match = direct ?? subgroup?.items.find(matches);

        if (!match) continue;
        trail.push({ label: group.title });

        if (subgroup) trail.push({ label: subgroup.title });
        item = match;
        break;
    }

    if (!item || item.path === RoutePath.main) return trail;

    trail.push({
        label: item.title,
        path: item.path
    });

    const suffix = pathname.slice(item.path.length).split('/').filter(Boolean);

    if (suffix[0] === 'new') trail.push({ label: 'Создание' });
    else if (suffix.length > 0) {
        trail.push({
            label: 'Карточка',
            path: item.path + '/' + suffix[0]
        });

        if (suffix[1] === 'edit') trail.push({ label: 'Редактирование' });
    }

    return trail;
};

